import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
    Plus,
    Pencil,
    Trash2,
    RefreshCw,
    ShieldCheck,
    ShieldAlert,
    Search,
    CheckCircle2,
    XCircle,
    Cpu,
    Calendar,
    AlertCircle,
    CheckCircle,
    Loader2,
    Filter,
    Layers,
    Lock,
    Unlock,
    X,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
    listAccessGroups,
    getAccessGroup,
    deleteAccessGroup,
} from '../services/accessGroupApi';
import CreateAccessGroupModal from '../components/CreateAccessGroupModal';

const EFFECT = { Deny: 0, Allow: 1 };

function usePermissions() {
    const { permissions = [] } = useAuth() || {};
    const has = (key) => permissions?.includes(key);
    return {
        canViewList: has('access_groups:view_list'),
        canViewDetail: has('access_groups:view_detail'),
        canCreate: has('access_groups:create'),
        canUpdate: has('access_groups:update'),
        canDelete: has('access_groups:delete'),
        canDevicesList: has('devices:view_list'),
    };
}

export default function AccessGroupsPage() {
    const perms = usePermissions();

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState('');
    const [isActive, setIsActive] = useState(''); // '', 'true', 'false'
    const [page, setPage] = useState(1);
    const [pageSize] = useState(50);

    // Bildirim state'leri
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);

    // Create/Edit Modal state
    const [openModal, setOpenModal] = useState(false);
    const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit'
    const [editId, setEditId] = useState(null);
    const [editGroup, setEditGroup] = useState(null);
    const [loadingDetailId, setLoadingDetailId] = useState(null);

    // Silme Onay Modalı state
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const stats = useMemo(() => {
        const total = items.length;
        const activeCount = items.filter((x) => x.isActive).length;
        const passiveCount = total - activeCount;
        const totalDevices = items.reduce((acc, cur) => acc + (Number(cur.scopeCount) || 0), 0);
        return { total, activeCount, passiveCount, totalDevices };
    }, [items]);

    const load = useCallback(async () => {
        if (!perms.canViewList) {
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const isActiveParam = isActive === '' ? undefined : isActive === 'true';
            const data = await listAccessGroups({
                q: q?.trim(),
                isActive: isActiveParam,
                page,
                pageSize,
            });
            setItems(Array.isArray(data?.items) ? data.items : []);
        } catch (err) {
            console.error(err);
            setError(err?.message || 'Yetki grupları yüklenirken bir hata oluştu.');
        } finally {
            setLoading(false);
        }
    }, [perms.canViewList, q, isActive, page, pageSize]);

    useEffect(() => {
        load();
    }, [load]);

    function openCreateModal() {
        setError(null);
        setMessage(null);
        setModalMode('create');
        setEditId(null);
        setEditGroup(null);
        setOpenModal(true);
    }

    async function openEditModal(id) {
        if (!perms.canViewDetail) {
            setError('Detay görüntüleme yetkiniz bulunmamaktadır.');
            return;
        }
        setError(null);
        setMessage(null);
        setLoadingDetailId(id);
        try {
            const dto = await getAccessGroup(id);
            setEditId(id);
            setEditGroup(dto);
            setModalMode('edit');
            setOpenModal(true);
        } catch (e) {
            console.error(e);
            setError(e?.message || 'Yetki grubu detayları alınamadı.');
        } finally {
            setLoadingDetailId(null);
        }
    }

    async function confirmDelete() {
        if (!perms.canDelete || !deleteTarget) return;
        setDeleting(true);
        setError(null);
        try {
            await deleteAccessGroup(deleteTarget.id, { hard: false });
            setMessage(`"${deleteTarget.name}" yetki grubu başarıyla silindi.`);
            setDeleteTarget(null);
            await load();
            setTimeout(() => setMessage(null), 4000);
        } catch (err) {
            console.error(err);
            setError(err?.message || 'Silme işleminde hata oluştu.');
        } finally {
            setDeleting(false);
        }
    }

    if (!perms.canViewList && !loading) {
        return (
            <div className="p-6">
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/60 text-amber-800 dark:text-amber-200 p-5 rounded-2xl flex items-center gap-3 max-w-2xl mx-auto shadow-sm">
                    <ShieldAlert className="w-6 h-6 text-amber-500 shrink-0" />
                    <span className="text-sm font-medium">
                        Bu sayfayı görüntüleme yetkiniz bulunmamaktadır.
                    </span>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 p-1 md:p-2">
            {/* Üst Bilgi & İstatistik Kartları */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-100 dark:border-gray-700/80 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-400">
                            Toplam Grup
                        </p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1 tabular-nums">
                            {stats.total}
                        </p>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                            Tanımlı geçiş yetki grubu
                        </p>
                    </div>
                    <div className="p-3 rounded-2xl bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400">
                        <Layers className="w-6 h-6" />
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-100 dark:border-gray-700/80 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-400">
                            Aktif Gruplar
                        </p>
                        <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-1 tabular-nums">
                            {stats.activeCount}
                        </p>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                            Kullanımda olan gruplar
                        </p>
                    </div>
                    <div className="p-3 rounded-2xl bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400">
                        <CheckCircle2 className="w-6 h-6" />
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-100 dark:border-gray-700/80 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-400">
                            Pasif Gruplar
                        </p>
                        <p className="text-2xl font-bold text-amber-600 dark:text-amber-400 mt-1 tabular-nums">
                            {stats.passiveCount}
                        </p>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                            Devre dışı bırakılanlar
                        </p>
                    </div>
                    <div className="p-3 rounded-2xl bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400">
                        <XCircle className="w-6 h-6" />
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-100 dark:border-gray-700/80 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-400">
                            Eşleşen Cihaz Kapsamı
                        </p>
                        <p className="text-2xl font-bold text-blue-600 dark:text-blue-400 mt-1 tabular-nums">
                            {stats.totalDevices}
                        </p>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                            Toplam kapı / okuyucu kuralı
                        </p>
                    </div>
                    <div className="p-3 rounded-2xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                        <Cpu className="w-6 h-6" />
                    </div>
                </div>
            </div>

            {/* Bildirim Mesajları */}
            {message && (
                <div className="bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800/70 text-emerald-800 dark:text-emerald-200 px-4 py-3 rounded-2xl flex items-center justify-between shadow-sm animate-fade-in-down">
                    <div className="flex items-center gap-2.5 text-sm font-medium">
                        <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0" />
                        <span>{message}</span>
                    </div>
                    <button
                        type="button"
                        onClick={() => setMessage(null)}
                        className="p-1 rounded-lg hover:bg-emerald-100 dark:hover:bg-emerald-800/50 text-emerald-600 dark:text-emerald-300"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}

            {error && (
                <div className="bg-rose-50 dark:bg-rose-900/30 border border-rose-200 dark:border-rose-800/70 text-rose-800 dark:text-rose-200 px-4 py-3 rounded-2xl flex items-center justify-between shadow-sm animate-fade-in-down">
                    <div className="flex items-center gap-2.5 text-sm font-medium">
                        <AlertCircle className="w-5 h-5 text-rose-500 shrink-0" />
                        <span>{error}</span>
                    </div>
                    <button
                        type="button"
                        onClick={() => setError(null)}
                        className="p-1 rounded-lg hover:bg-rose-100 dark:hover:bg-rose-800/50 text-rose-600 dark:text-rose-300"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}

            {/* Ana Kart: Filtre Barı + Tablo */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700/80 overflow-hidden">
                {/* Üst Araç Çubuğu */}
                <div className="p-4 md:p-5 border-b border-gray-100 dark:border-gray-700/70 bg-gray-50/50 dark:bg-gray-800/60 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            setPage(1);
                            load();
                        }}
                        className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-1"
                    >
                        {/* Arama Kutusu */}
                        <div className="relative flex-1 max-w-md">
                            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                            <input
                                type="text"
                                className="w-full pl-10 pr-9 py-2.5 text-sm rounded-xl bg-white dark:bg-gray-900/70 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 transition-all"
                                placeholder="Grup adı veya açıklama ile ara…"
                                value={q}
                                onChange={(e) => {
                                    setQ(e.target.value);
                                    setPage(1);
                                }}
                            />
                            {q && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setQ('');
                                        setPage(1);
                                    }}
                                    className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                                    title="Aramayı temizle"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>

                        {/* Durum Filtresi (Segmented Pill + Select uyumlu) */}
                        <div className="inline-flex items-center p-1 rounded-xl bg-gray-100 dark:bg-gray-900/80 border border-gray-200/80 dark:border-gray-700/80 self-start sm:self-auto">
                            {[
                                { value: '', label: 'Tümü' },
                                { value: 'true', label: 'Aktif' },
                                { value: 'false', label: 'Pasif' },
                            ].map((tab) => {
                                const active = isActive === tab.value;
                                return (
                                    <button
                                        key={tab.value}
                                        type="button"
                                        onClick={() => {
                                            setIsActive(tab.value);
                                            setPage(1);
                                        }}
                                        className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                                            active
                                                ? 'bg-white dark:bg-gray-700 text-indigo-600 dark:text-indigo-300 shadow-sm'
                                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                                        }`}
                                    >
                                        {tab.label}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Yenile Butonu */}
                        <button
                            type="submit"
                            disabled={loading}
                            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium bg-white dark:bg-gray-900/70 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/70 transition-all shadow-sm disabled:opacity-60"
                            title="Listeyi Yenile"
                        >
                            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-indigo-500' : ''}`} />
                            <span>Yenile</span>
                        </button>
                    </form>

                    {/* Yeni Grup Ekle Butonu */}
                    {perms.canCreate && (
                        <button
                            type="button"
                            onClick={openCreateModal}
                            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white shadow-md shadow-indigo-500/20 transition-all active:scale-[0.99]"
                        >
                            <Plus className="w-4 h-4 stroke-[2.5]" />
                            <span>Yeni Yetki Grubu</span>
                        </button>
                    )}
                </div>

                {/* Tablo Alanı */}
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50/90 dark:bg-gray-900/50 border-b border-gray-100 dark:border-gray-700/70">
                            <tr>
                                <th className="text-left py-3.5 px-5 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                    Grup Adı
                                </th>
                                <th className="text-left py-3.5 px-4 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                    Açıklama
                                </th>
                                <th className="text-left py-3.5 px-4 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                    Durum
                                </th>
                                <th className="text-left py-3.5 px-4 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                    Varsayılan Etki
                                </th>
                                <th className="text-center py-3.5 px-4 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                    Cihaz Kapsamı
                                </th>
                                <th className="text-right py-3.5 px-4 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                    Oluşturulma
                                </th>
                                <th className="text-right py-3.5 px-5 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                    İşlemler
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
                            {loading ? (
                                <tr>
                                    <td colSpan={7} className="py-14 text-center">
                                        <div className="inline-flex flex-col items-center gap-2.5 text-gray-500 dark:text-gray-400">
                                            <Loader2 className="w-7 h-7 animate-spin text-indigo-500" />
                                            <span className="text-sm font-medium">Yetki grupları yükleniyor…</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : items.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="py-16 text-center">
                                        <div className="max-w-sm mx-auto flex flex-col items-center">
                                            <div className="p-3.5 rounded-2xl bg-indigo-50 dark:bg-indigo-900/20 text-indigo-500 dark:text-indigo-400 mb-3">
                                                <ShieldCheck className="w-8 h-8" />
                                            </div>
                                            <p className="text-base font-semibold text-gray-800 dark:text-gray-100">
                                                Yetki grubu bulunamadı
                                            </p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                Arama kriterlerinize uygun kayıt yok veya henüz bir geçiş yetki grubu tanımlanmamış.
                                            </p>
                                            {perms.canCreate && (
                                                <button
                                                    type="button"
                                                    onClick={openCreateModal}
                                                    className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm transition-all"
                                                >
                                                    <Plus className="w-4 h-4" />
                                                    İlk Yetki Grubunu Oluştur
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                items.map((g) => {
                                    const isRowLoading = loadingDetailId === g.id;
                                    return (
                                        <tr
                                            key={g.id}
                                            className="hover:bg-indigo-50/40 dark:hover:bg-gray-700/40 transition-colors group"
                                        >
                                            <td className="py-3.5 px-5">
                                                <div className="flex items-center gap-3">
                                                    <div
                                                        className={`p-2 rounded-xl shrink-0 ${
                                                            g.isActive
                                                                ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400'
                                                                : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
                                                        }`}
                                                    >
                                                        <ShieldCheck className="w-4 h-4" />
                                                    </div>
                                                    <div>
                                                        <span className="font-semibold text-gray-900 dark:text-white block">
                                                            {g.name}
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>

                                            <td className="py-3.5 px-4 text-gray-600 dark:text-gray-300 max-w-xs truncate">
                                                {g.description || (
                                                    <span className="text-gray-400 dark:text-gray-500 italic text-xs">
                                                        Açıklama belirtilmemiş
                                                    </span>
                                                )}
                                            </td>

                                            <td className="py-3.5 px-4">
                                                {g.isActive ? (
                                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 border border-emerald-200/60 dark:border-emerald-700/50">
                                                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                                        Aktif
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 border border-gray-200/60 dark:border-gray-600">
                                                        <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                                                        Pasif
                                                    </span>
                                                )}
                                            </td>

                                            <td className="py-3.5 px-4">
                                                {g.defaultEffect === EFFECT.Allow ? (
                                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 border border-blue-200/60 dark:border-blue-700/50">
                                                        <Unlock className="w-3 h-3" />
                                                        Liste dışı: Geçebilir
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border border-amber-200/60 dark:border-amber-700/50">
                                                        <Lock className="w-3 h-3" />
                                                        Liste dışı: Geçemez
                                                    </span>
                                                )}
                                            </td>

                                            <td className="py-3.5 px-4 text-center">
                                                <span className="inline-flex items-center justify-center min-w-[2.25rem] px-2.5 py-1 rounded-xl text-xs font-bold bg-gray-100 dark:bg-gray-700/80 text-gray-800 dark:text-gray-200 tabular-nums">
                                                    {g.scopeCount ?? 0}
                                                </span>
                                            </td>

                                            <td className="py-3.5 px-4 text-right text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                                {g.createdDate ? (
                                                    <span className="inline-flex items-center gap-1.5">
                                                        <Calendar className="w-3.5 h-3.5 opacity-60" />
                                                        {new Date(g.createdDate).toLocaleString('tr-TR', {
                                                            day: '2-digit',
                                                            month: '2-digit',
                                                            year: 'numeric',
                                                            hour: '2-digit',
                                                            minute: '2-digit',
                                                        })}
                                                    </span>
                                                ) : (
                                                    '-'
                                                )}
                                            </td>

                                            <td className="py-3.5 px-5">
                                                <div className="flex items-center gap-2 justify-end">
                                                    {perms.canUpdate && (
                                                        <button
                                                            type="button"
                                                            onClick={() => openEditModal(g.id)}
                                                            disabled={isRowLoading}
                                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-900/30 dark:hover:bg-indigo-900/50 text-indigo-600 dark:text-indigo-300 border border-indigo-200/60 dark:border-indigo-700/50 transition-all disabled:opacity-60"
                                                            title="Grubu ve Cihaz Kapsamını Düzenle"
                                                        >
                                                            {isRowLoading ? (
                                                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                            ) : (
                                                                <Pencil className="w-3.5 h-3.5" />
                                                            )}
                                                            <span>Düzenle</span>
                                                        </button>
                                                    )}
                                                    {perms.canDelete && (
                                                        <button
                                                            type="button"
                                                            onClick={() => setDeleteTarget(g)}
                                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold bg-rose-50 hover:bg-rose-100 dark:bg-rose-900/20 dark:hover:bg-rose-900/40 text-rose-600 dark:text-rose-300 border border-rose-200/60 dark:border-rose-800/50 transition-all"
                                                            title="Grubu Sil"
                                                        >
                                                            <Trash2 className="w-3.5 h-3.5" />
                                                            <span>Sil</span>
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Alt Bilgi Barı */}
                {!loading && items.length > 0 && (
                    <div className="px-5 py-3 bg-gray-50/60 dark:bg-gray-900/40 border-t border-gray-100 dark:border-gray-700/60 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                        <span>
                            Toplam <strong className="text-gray-800 dark:text-gray-200">{items.length}</strong> yetki grubu listeleniyor
                        </span>
                        <span>Sayfa {page}</span>
                    </div>
                )}
            </div>

            {/* Oluşturma / Düzenleme Modalı */}
            <CreateAccessGroupModal
                open={openModal}
                onClose={() => setOpenModal(false)}
                onSaved={() => {
                    setOpenModal(false);
                    setMessage(
                        modalMode === 'create'
                            ? 'Yeni yetki grubu başarıyla oluşturuldu.'
                            : 'Yetki grubu ve cihaz kapsamları güncellendi.'
                    );
                    load();
                    setTimeout(() => setMessage(null), 4000);
                }}
                canCreate={perms.canCreate}
                canUpdate={perms.canUpdate}
                mode={modalMode}
                groupId={editId}
                initialGroup={editGroup}
            />

            {/* Modern Silme Onay Modalı */}
            {deleteTarget && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div
                        className="fixed inset-0 bg-gray-900/70 backdrop-blur-sm transition-opacity"
                        onClick={() => (!deleting ? setDeleteTarget(null) : null)}
                    />
                    <div className="relative w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700/80 overflow-hidden z-10">
                        <div className="p-6">
                            <div className="flex items-start gap-4">
                                <div className="p-3 rounded-2xl bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 shrink-0">
                                    <Trash2 className="w-6 h-6" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                                        Yetki Grubunu Sil
                                    </h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-1.5 leading-relaxed">
                                        <span className="font-semibold text-gray-900 dark:text-white">
                                            "{deleteTarget.name}"
                                        </span>{' '}
                                        adlı yetki grubunu silmek istediğinize emin misiniz?
                                    </p>
                                </div>
                            </div>

                            <div className="mt-6 flex items-center justify-end gap-2.5">
                                <button
                                    type="button"
                                    disabled={deleting}
                                    onClick={() => setDeleteTarget(null)}
                                    className="px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 bg-gray-50 dark:bg-gray-700/60 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                >
                                    Vazgeç
                                </button>
                                <button
                                    type="button"
                                    disabled={deleting}
                                    onClick={confirmDelete}
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-rose-600 hover:bg-rose-700 text-white shadow-md shadow-rose-500/20 transition-all disabled:opacity-60"
                                >
                                    {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
                                    <span>Evet, Sil</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
